// src/scripts/sqlApiHelper/mailerApi.js
import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";

const mailerApiCallable = httpsCallable(functions, "mailerApi", {
  timeout: 3600000,
});

function clean(value) {
  return String(value ?? "").trim();
}

async function callMailer(action, payload = {}) {
  const result = await mailerApiCallable({
    action,
    ...payload,
  });

  return result?.data || {};
}

export async function getMailerBootstrap(venueId) {
  return callMailer("bootstrap", {
    venueId: clean(venueId),
  });
}

export async function listAllMailerGuests(venueId) {
  const all = [];
  let cursor = "";

  do {
    const result = await callMailer("listGuests", {
      venueId: clean(venueId),
      cursor,
      pageSize: 500,
    });

    all.push(...(Array.isArray(result.guests) ? result.guests : []));
    cursor = clean(result.nextCursor);
  } while (cursor);

  return all;
}


/**
 * Returns a real guest record for PDF merge-field proofing.
 *
 * Preference:
 * - subscribed guest with a first name
 * - otherwise any subscribed guest
 * - otherwise null
 *
 * This uses the existing paginated guest API, so no Cloud Function
 * deployment is required for this proof-only change.
 */
export async function getMailerProofGuest(venueId) {
  const cleanVenueId = clean(venueId);

  if (!cleanVenueId) {
    return null;
  }

  let cursor = "";
  let firstSubscribed = null;

  do {
    const result = await callMailer("listGuests", {
      venueId: cleanVenueId,
      cursor,
      pageSize: 100,
    });

    const rows = Array.isArray(result.guests) ? result.guests : [];

    for (const guest of rows) {
      if (guest?.subscribed !== true) {
        continue;
      }

      if (!firstSubscribed) {
        firstSubscribed = guest;
      }

      if (clean(guest?.first_name)) {
        return guest;
      }
    }

    cursor = clean(result.nextCursor);
  } while (cursor);

  return firstSubscribed;
}

export async function upsertMailerGuests(venueId, guests) {
  const rows = Array.isArray(guests) ? guests : [];

  const summary = {
    received: rows.length,
    created: 0,
    updated: 0,
    skipped: 0,
  };

  for (let i = 0; i < rows.length; i += 350) {
    const chunk = rows.slice(i, i + 350);

    const result = await callMailer("upsertGuests", {
      venueId: clean(venueId),
      guests: chunk,
    });

    summary.created += Number(result.created || 0);
    summary.updated += Number(result.updated || 0);
    summary.skipped += Number(result.skipped || 0);
  }

  return summary;
}

export async function deleteMailerGuest(venueId, guestId) {
  return callMailer("deleteGuest", {
    venueId: clean(venueId),
    guestId: clean(guestId),
  });
}

export async function deleteMailerGuests(venueId, guestIds) {
  const ids = [
    ...new Set(
      (Array.isArray(guestIds) ? guestIds : [])
        .map((guestId) => clean(guestId))
        .filter(Boolean)
    ),
  ];

  const deletedIds = [];
  const failures = [];

  for (let index = 0; index < ids.length; index += 10) {
    const chunk = ids.slice(index, index + 10);

    const results = await Promise.allSettled(
      chunk.map((guestId) => deleteMailerGuest(venueId, guestId))
    );

    results.forEach((result, resultIndex) => {
      const guestId = chunk[resultIndex];

      if (result.status === "fulfilled") {
        deletedIds.push(guestId);
        return;
      }

      failures.push({
        guestId,
        message:
          result.reason?.message ||
          result.reason?.details ||
          "Delete failed.",
      });
    });
  }

  return {
    requested: ids.length,
    deleted: deletedIds.length,
    failed: failures.length,
    deletedIds,
    failures,
  };
}

export async function setMailerGuestSubscription(
  venueId,
  guestId,
  subscribed
) {
  return callMailer("setGuestSubscription", {
    venueId: clean(venueId),
    guestId: clean(guestId),
    subscribed: subscribed === true,
  });
}

export async function listMailerTemplates(venueId) {
  const result = await callMailer("listTemplates", {
    venueId: clean(venueId),
  });

  return Array.isArray(result.templates) ? result.templates : [];
}

export async function saveMailerTemplate(venueId, template) {
  return callMailer("saveTemplate", {
    venueId: clean(venueId),
    template,
  });
}

export async function deleteMailerTemplate(venueId, templateId) {
  return callMailer("deleteTemplate", {
    venueId: clean(venueId),
    templateId: clean(templateId),
  });
}

export async function uploadMailerImage(venueId, file) {
  if (!(file instanceof File)) {
    throw new Error("Please choose an image file.");
  }

  if (!String(file.type || "").startsWith("image/")) {
    throw new Error("Only image files can be uploaded.");
  }

  if (file.size > 5 * 1024 * 1024) {
    throw new Error("Images must be 5 MB or smaller.");
  }

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result || ""));

    reader.onerror = () =>
      reject(reader.error || new Error("Could not read image."));

    reader.readAsDataURL(file);
  });

  return callMailer("uploadImage", {
    venueId: clean(venueId),
    fileName: clean(file.name) || "image",
    contentType: clean(file.type) || "image/jpeg",
    dataUrl,
  });
}

/**
 * Loads a saved mailer image through the authenticated Cloud Function.
 *
 * This is used by the PDF proof generator so it does not depend on the
 * browser being allowed to fetch Firebase Storage download URLs directly.
 */
export async function getMailerImageData(venueId, storagePath) {
  const cleanVenueId = clean(venueId);
  const cleanStoragePath = clean(storagePath);

  if (!cleanVenueId) {
    throw new Error("Venue ID is required to load the mailer image.");
  }

  if (!cleanStoragePath) {
    throw new Error("Image storage path is required.");
  }

  return callMailer("getImageData", {
    venueId: cleanVenueId,
    storagePath: cleanStoragePath,
  });
}

export async function sendMailerCampaign(venueId, campaign) {
  return callMailer("sendCampaign", {
    venueId: clean(venueId),
    campaign,
  });
}

export async function listMailerCampaigns(venueId) {
  const result = await callMailer("listCampaigns", {
    venueId: clean(venueId),
  });

  return Array.isArray(result.campaigns) ? result.campaigns : [];
}
